import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarCheck, Facebook, Loader2, Mail, MapPin, Phone, PhoneCall, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { setSeoMetadata } from '../lib/seo';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useHeroMedia } from '../hooks/useHeroMedia';
import { CONTACT_PAGE, ROUTES } from '../data/stjSite';
import SlidePuzzleCaptcha from '../components/SlidePuzzleCaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { name: '', email: '', phone: '', reason: '', message: '', lastName: '' };

/**
 * /contacting-us-in-st-john - the legacy contact page, field for field.
 *
 * The form mirrors the live one exactly (name, email, phone, Reason For Contact, Questions /
 * Comments, all required, plus the hidden anti-spam field) and its consent wording is unchanged.
 * Submissions create a real lead through POST /api/leads/, so they land in the CRM instead of
 * disappearing. Email and the hero video come from Developer Settings, never hardcoded.
 */
const ContactStJohnPage = () => {
  const { siteName, supportEmail } = useSiteSettings();
  const hero = useHeroMedia();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(null);   // {token, x} once the puzzle is solved
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const description = 'Contact St John Travel & Life for free concierge help, questions about a '
      + 'listing or activity, or advertising on the island directory. Call or text (340) 513-7080.';
    setSeoMetadata({
      title: CONTACT_PAGE.heading,
      description,
      keywords: 'contact st john travel and life, st john concierge, st john vi help line, reserve st john contact',
      canonicalPath: ROUTES.contactUs,
      ogType: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: CONTACT_PAGE.heading,
        description,
        mainEntity: {
          '@type': 'Organization',
          name: siteName || 'St John Travel & Life',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'P.O. Box 1797',
            addressLocality: 'St John',
            addressRegion: 'VI',
            postalCode: '00831',
            addressCountry: 'US',
          },
          contactPoint: CONTACT_PAGE.phones.map((phone) => ({
            '@type': 'ContactPoint',
            telephone: phone.number,
            contactType: phone.label,
            areaServed: 'US',
            availableLanguage: 'English',
          })),
        },
      },
    });
  }, [siteName]);

  const update = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    // the hidden field is only ever filled in by a bot
    if (form.lastName) {
      setSent(true);
      return;
    }
    if (!captcha) {
      setError('Please slide the puzzle piece into the gap so we know you are human.');
      return;
    }
    setSending(true);
    try {
      // one endpoint: it checks the puzzle, saves the opportunity in the CRM and emails the office
      const res = await axios.post(`${API_URL}/api/contact/submit`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        reason: form.reason,
        message: form.message,
        captcha_token: captcha.token,
        captcha_x: captcha.x,
        honeypot: form.lastName,
        page: ROUTES.contactUs,
      });
      setSent(true);
      setForm(EMPTY);
      setCaptcha(null);
      if (res.data && res.data.emailed === false) {
        // the enquiry is safe in the CRM either way - this only tells us mail is not set up yet
        console.info('Contact enquiry saved; email not sent:', res.data.email_error);
      }
    } catch (submitError) {
      const detail = submitError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
        : 'Sorry, that did not go through. Please call or text (340) 513-7080 and we will help right away.');
      setCaptcha(null);
    } finally {
      setSending(false);
    }
  };

  const inputClass = 'w-full rounded border border-slate-300 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 outline-none focus:border-blue-600';

  return (
    <div className="min-h-screen bg-white" data-testid="contact-st-john-page">
      <Header />

      {/* ================= HERO ================= */}
      <section className="relative flex min-h-[46vh] items-center justify-center overflow-hidden lg:min-h-[56vh]" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100 via-white to-white" />
        {hero.videoUrl ? (
          <video
            key={hero.videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={hero.backgroundImageUrl || undefined}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="contact-hero-video"
          >
            <source src={hero.videoUrl} type={hero.videoMimeType} />
          </video>
        ) : hero.backgroundImageUrl ? (
          <div className="absolute inset-0 h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${hero.backgroundImageUrl})` }} />
        ) : null}
        <div className="absolute inset-0 bg-black/25" />

        <div className="relative z-10 w-full px-4 py-24 text-center">
          <h1 className="text-4xl font-extrabold italic tracking-tight text-white drop-shadow-[0_3px_10px_rgba(0,0,0,0.75)] sm:text-5xl lg:text-6xl">
            {CONTACT_PAGE.heading}
          </h1>
          <h2 className="mt-4 text-xl font-semibold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.75)] sm:text-2xl">
            {CONTACT_PAGE.subheading}
          </h2>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-12 lg:py-16">
        <h2 className="text-center text-3xl font-extrabold uppercase italic tracking-tight text-slate-900 sm:text-4xl" data-testid="contact-tagline">
          {CONTACT_PAGE.tagline}
        </h2>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.4fr,1fr]">
          {/* ---------------- form ---------------- */}
          <section>
            <h3 className="mb-5 text-xl font-bold text-slate-900" data-testid="contact-form-title">{CONTACT_PAGE.formTitle}</h3>

            {sent ? (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-6" data-testid="contact-form-success">
                <p className="text-lg font-bold text-emerald-800">Thank you &mdash; your message is on its way.</p>
                <p className="mt-2 text-emerald-900/80">
                  We answer fast. If it is urgent, call or text the help line on{' '}
                  <a href={CONTACT_PAGE.phones[0].tel} className="font-semibold underline">{CONTACT_PAGE.phones[0].number}</a>.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="mt-4 rounded border-2 border-emerald-600 px-5 py-2 text-sm font-bold uppercase tracking-wide text-emerald-700 hover:bg-emerald-100"
                  data-testid="contact-form-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-semibold text-slate-700">Enter your name *</label>
                  <input id="contact-name" required value={form.name} onChange={update('name')} placeholder="Enter your name" className={inputClass} data-testid="contact-name" />
                </div>

                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm font-semibold text-slate-700">Enter your email address *</label>
                  <input id="contact-email" type="email" required value={form.email} onChange={update('email')} placeholder="Enter your email address" className={inputClass} data-testid="contact-email" />
                </div>

                <div>
                  <label htmlFor="contact-phone" className="mb-1.5 block text-sm font-semibold text-slate-700">Phone *</label>
                  <input id="contact-phone" required value={form.phone} onChange={update('phone')} className={inputClass} data-testid="contact-phone" />
                </div>

                <div>
                  <label htmlFor="contact-reason" className="mb-1.5 block text-sm font-semibold text-slate-700">Reason For Contact *</label>
                  <select id="contact-reason" required value={form.reason} onChange={update('reason')} className={inputClass} data-testid="contact-reason">
                    <option value="">Reason For Contact</option>
                    {CONTACT_PAGE.reasons.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-sm font-semibold text-slate-700">Questions / Comments *</label>
                  <textarea id="contact-message" required rows={3} value={form.message} onChange={update('message')} className={inputClass} data-testid="contact-message" />
                </div>

                {/* anti-spam: hidden from people, irresistible to bots */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="contact-last-name">Last Name</label>
                  <input id="contact-last-name" tabIndex={-1} autoComplete="off" value={form.lastName} onChange={update('lastName')} />
                </div>

                <SlidePuzzleCaptcha onSolved={setCaptcha} onReset={() => setCaptcha(null)} />

                {error && <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="contact-form-error">{error}</p>}

                <button
                  type="submit"
                  disabled={sending || !captcha}
                  className="inline-flex items-center gap-2 rounded bg-red-600 px-8 py-4 font-bold uppercase tracking-wide text-white transition hover:bg-red-700 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {sending ? 'Sending...' : 'Submit'}
                </button>

                <p className="text-xs leading-relaxed text-slate-500" data-testid="contact-consent">
                  {CONTACT_PAGE.consent}
                </p>
                <p className="text-xs text-slate-500">
                  <Link to="/privacy-policy" className="underline hover:text-blue-700">Privacy Policy</Link>
                  {' \u00b7 '}
                  <Link to="/terms-conditions" className="underline hover:text-blue-700">Terms of use</Link>
                </p>
              </form>
            )}
          </section>

          {/* ---------------- contact details ---------------- */}
          <aside className="space-y-6" data-testid="contact-details">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              {supportEmail && (
                <a href={`mailto:${supportEmail}`} className="flex items-start gap-3 text-slate-700 hover:text-blue-700" data-testid="contact-email-link">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                  <span className="font-semibold">{supportEmail}</span>
                </a>
              )}

              <p className="mt-4 flex items-start gap-3 text-slate-700" data-testid="contact-address">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                <span>{CONTACT_PAGE.mailingAddress}</span>
              </p>

              {CONTACT_PAGE.phones.map((phone) => (
                <a key={phone.number} href={phone.tel} className="mt-4 flex items-start gap-3 text-slate-700 hover:text-blue-700" data-testid={`contact-phone-${phone.number.replace(/\D/g, '')}`}>
                  <Phone className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                  <span>
                    <span className="block font-bold">{phone.number}</span>
                    <span className="text-sm text-slate-500">{phone.label}</span>
                  </span>
                </a>
              ))}

              <a
                href={CONTACT_PAGE.facebookGroup}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-slate-700 hover:text-blue-700"
                data-testid="contact-facebook"
              >
                <Facebook className="h-5 w-5 text-blue-700" /> Facebook Group
              </a>
            </div>

            <a
              href={CONTACT_PAGE.phones[0].tel}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-800 px-6 py-4 font-bold uppercase tracking-wide text-white transition hover:bg-blue-900"
              data-testid="contact-click-to-call"
            >
              <PhoneCall className="h-5 w-5" /> Click to Call {CONTACT_PAGE.phones[0].number}
            </a>

            {/* internal booking page by default, external scheduler if a full URL is configured */}
            {/^https?:\/\//i.test(CONTACT_PAGE.scheduleCall) ? (
              <a
                href={CONTACT_PAGE.scheduleCall}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-blue-800 bg-white px-6 py-4 text-center font-bold uppercase tracking-wide text-blue-800 transition hover:bg-blue-50"
                data-testid="contact-schedule-call"
              >
                <CalendarCheck className="h-5 w-5" /> Schedule Complimentary Call
              </a>
            ) : (
              <Link
                to={CONTACT_PAGE.scheduleCall}
                className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-blue-800 bg-white px-6 py-4 text-center font-bold uppercase tracking-wide text-blue-800 transition hover:bg-blue-50"
                data-testid="contact-schedule-call"
              >
                <CalendarCheck className="h-5 w-5" /> Schedule Complimentary Call
              </Link>
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactStJohnPage;
